import { findBlock } from "./find";
import { isGroup, isRow } from "./sections";
import type { EmailDocument, Selection } from "./types";

function collectBlockSnapshots(
  document: EmailDocument,
): Map<string, string> {
  const blocks = new Map<string, string>();
  for (const section of document.sections) {
    const rows = isRow(section) ? [section] : isGroup(section) ? section.rows : [];
    for (const row of rows) {
      for (const column of row.columns) {
        for (const block of column.blocks) {
          blocks.set(block.id, JSON.stringify(block));
        }
      }
    }
  }
  return blocks;
}

export function collectHistoryAffectedBlockIds(
  before: EmailDocument,
  after: EmailDocument,
): string[] {
  const beforeBlocks = collectBlockSnapshots(before);
  const afterBlocks = collectBlockSnapshots(after);
  const affected: string[] = [];

  for (const [blockId, afterSnapshot] of afterBlocks) {
    const beforeSnapshot = beforeBlocks.get(blockId);
    if (beforeSnapshot === undefined || beforeSnapshot !== afterSnapshot) {
      affected.push(blockId);
    }
  }

  for (const blockId of beforeBlocks.keys()) {
    if (!afterBlocks.has(blockId)) {
      affected.push(blockId);
    }
  }

  return affected;
}

export function findHistoryRevealBlockId(
  before: EmailDocument,
  after: EmailDocument,
  preferredBlockId: string | null = null,
): string | null {
  const affected = collectHistoryAffectedBlockIds(before, after);
  if (affected.length === 0) {
    return null;
  }

  const afterBlocks = collectBlockSnapshots(after);
  if (
    preferredBlockId !== null &&
    affected.includes(preferredBlockId) &&
    afterBlocks.has(preferredBlockId)
  ) {
    return preferredBlockId;
  }

  for (const blockId of affected) {
    if (afterBlocks.has(blockId)) {
      return blockId;
    }
  }

  return null;
}

export function selectionForBlockId(
  document: EmailDocument,
  blockId: string,
): Selection | null {
  for (const section of document.sections) {
    const rows = isRow(section) ? [section] : isGroup(section) ? section.rows : [];
    for (const row of rows) {
      for (const column of row.columns) {
        for (const block of column.blocks) {
          if (block.id !== blockId) {
            continue;
          }
          if (
            findBlock(document, row.id, column.id, block.id) === undefined
          ) {
            return null;
          }
          return {
            kind: "block",
            rowId: row.id,
            columnId: column.id,
            blockId: block.id,
          };
        }
      }
    }
  }
  return null;
}

export function didEmailSettingsChange(
  before: EmailDocument,
  after: EmailDocument,
): boolean {
  return JSON.stringify(before.settings) !== JSON.stringify(after.settings);
}
